/** @type {typeof window.requestIdleCallback} */
export const requestIdleCallback =
  typeof window.requestIdleCallback === 'function'
    ? window.requestIdleCallback.bind(window)
    : (callback) =>
      window.setTimeout(() => {
        callback(
            /** @type {IdleDeadline} */({
            didTimeout: false,
            timeRemaining: () => 0,
          })
        );
      }, 1);

/**
 * @param {() => void} callback
 */
export function onDocumentReady(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback);
  } else {
    callback();
  }
}

/**
 * @param {string} [type]
 * @param {{ headers?: Record<string, string>, body?: BodyInit | null }} [config]
 * @returns {RequestInit}
 */
export function fetchConfig(type = 'json', config = {}) {
  /** @type {Record<string, string>} */
  const headers = {
    'Content-Type': 'application/json',
    Accept: `application/${type}`,
    ...(config.headers || {}),
  };

  if (type === 'javascript') {
    headers['X-Requested-With'] = 'XMLHttpRequest';
    delete headers['Content-Type'];
  }

  return {
    method: 'POST',
    headers,
    body: config.body,
  };
}

/**
 * @template {(...args: any[]) => void} T
 * @param {T} fn
 * @param {number} wait
 * @returns {T & { cancel: () => void }}
 */
export function debounce(fn, wait) {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timeout;

  /**
   * @param {...any} args
   */
  const debounced = function (...args) {
    if (timeout !== undefined) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };

  debounced.cancel = () => {
    if (timeout !== undefined) {
      clearTimeout(timeout);
    }
  };

  return /** @type {T & { cancel: () => void }} */ (debounced);
}

/**
 * @template {(...args: any[]) => void} T
 * @param {T} fn
 * @param {number} delay
 * @returns {T & { cancel: () => void }}
 */
export function throttle(fn, delay) {
  let lastCall = 0;

  /**
   * @param {...any} args
   */
  const throttled = function (...args) {
    const now = performance.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn.apply(this, args);
    }
  };

  throttled.cancel = () => {
    lastCall = performance.now();
  };

  return /** @type {T & { cancel: () => void }} */ (throttled);
}

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

export function prefersReducedMotion() {
  return reducedMotion.matches;
}

export const mediaQueryLarge = matchMedia('(min-width: 750px)');

export function isMobileBreakpoint() {
  return !mediaQueryLarge.matches;
}

/**
 * @param {Element | Element[]} elements
 * @param {(() => void) | undefined} callback
 * @param {GetAnimationsOptions} [options]
 */
export function onAnimationEnd(elements, callback, options = { subtree: true }) {
  const list = Array.isArray(elements) ? elements : [elements];
  const animationPromises = list.flatMap((element) =>
    element
      .getAnimations(options)
      .filter((animation) => animation.timeline instanceof DocumentTimeline)
      .map((animation) => animation.finished)
  );

  return Promise.allSettled(animationPromises).then(() => {
    if (callback) callback();
  });
}

/**
 * @param {number} x
 * @param {number} y
 * @param {Element} element
 */
export function isPointWithinElement(x, y, element) {
  const { left, right, top, bottom } = element.getBoundingClientRect();
  return x >= left && x <= right && y >= top && y <= bottom;
}

/**
 * @param {MouseEvent} event
 * @param {Element} element
 */
export function isClickedOutside(event, element) {
  if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) {
    return !isPointWithinElement(event.clientX, event.clientY, element);
  }

  const eventPath = typeof event.composedPath === 'function' ? event.composedPath() : [];

  if (eventPath.includes(element)) {
    return false;
  }

  return !element.contains(event.target);
}

/**
 * @template {number | null} T
 * @param {string | number | null | undefined} value
 * @param {T} defaultValue
 * @returns {number | T}
 */
export function parseIntOrDefault(value, defaultValue) {
  if (value === null || value === undefined || value === '') return defaultValue;
  const parsed = parseInt(value.toString(), 10);
  return Number.isNaN(parsed) ? defaultValue : parsed;
}

class Scheduler {
  /**
   * @param {() => void} task
   */
  schedule(task) {
    requestAnimationFrame(() => setTimeout(task, 0));
  }
}

export const scheduler = new Scheduler();

if (typeof Theme !== 'undefined') {
  Theme.utilities = {
    ...Theme.utilities,
    scheduler,
  };
}
